'use client';

import './tooltip.scss';

import * as RdxTooltip from '@radix-ui/react-tooltip';
import React, { createContext, useContext, useState } from 'react';

import { cn, isOnDesktop } from '../../utils/helpers';

const enableClicks = !isOnDesktop();

/**
 * Tooltip Global Provider.
 */
interface TooltipProviderProps extends React.PropsWithChildren {
  delayDuration?: RdxTooltip.TooltipProviderProps['delayDuration'];
  skipDelayDuration?: RdxTooltip.TooltipProviderProps['skipDelayDuration'];
  disableHoverableContent?: RdxTooltip.TooltipProviderProps['disableHoverableContent'];
}
export const TooltipProvider: React.FC<TooltipProviderProps> = ({
  children,
  delayDuration = 200,
  disableHoverableContent,
  skipDelayDuration,
}) => {
  return (
    <RdxTooltip.Provider
      delayDuration={enableClicks ? 0 : delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      {children}
    </RdxTooltip.Provider>
  );
};

/**
 * Trigger Trigger Context
 */
interface TooltipTriggerContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  isOpen: false,
  setIsOpen: () => {},
});
const ToolTipTriggerProvider = TooltipTriggerContext.Provider;

/**
 * Tooltip
 */
export interface TooltipProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Sets the initial open state for uncontrolled tooltips.
   * After mount, the tooltip manages its own state and can still be opened or closed by user interaction.
   */
  isDefaultOpen?: RdxTooltip.TooltipProps['defaultOpen'];
  /**
   * Controls the open state of the tooltip.
   * When provided, the parent owns the state and must update `isOpen` in response to `onOpenChange`.
   */
  isOpen?: RdxTooltip.TooltipProps['open'];
  /**
   * Called whenever the tooltip requests an open-state change.
   * Use this with `isOpen` for controlled tooltips instead of relying on trigger click events alone.
   */
  onOpenChange?: RdxTooltip.TooltipProps['onOpenChange'];
}

/**
 * Supports both uncontrolled and controlled usage.
 * Use `isDefaultOpen` when the tooltip should start open but remain dismissible.
 * Use `isOpen` with `onOpenChange` when the parent should fully own the tooltip state.
 */
export const Tooltip = ({
  children,
  isDefaultOpen = undefined,
  isOpen,
  onOpenChange,
  ...props
}: TooltipProps) => {
  // Implementing local state to allow mobile clicks to toggle open/locse
  // while also allowing parents to control state of the tooltip.
  const isControlled = isOpen !== undefined;
  const [uncontrolledOpenState, setUncontrolledOpenState] = useState(
    isDefaultOpen ?? false,
  );
  const openState = isControlled ? isOpen : uncontrolledOpenState;
  const setIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (
    nextOpenState,
  ) => {
    const resolvedOpenState =
      typeof nextOpenState === 'function'
        ? nextOpenState(openState)
        : nextOpenState;

    if (!isControlled) {
      setUncontrolledOpenState(resolvedOpenState);
    }

    onOpenChange?.(resolvedOpenState);
  };

  return (
    <RdxTooltip.Root open={openState} onOpenChange={setIsOpen} {...props}>
      <ToolTipTriggerProvider value={{ isOpen: !!openState, setIsOpen }}>
        {children}
      </ToolTipTriggerProvider>
    </RdxTooltip.Root>
  );
};

export type TooltipTriggerProps = RdxTooltip.TooltipTriggerProps;

export const TooltipTrigger = ({
  children,
  asChild = true,
  onClick,
  onPointerDown,
  ...props
}: TooltipTriggerProps) => {
  const { isOpen, setIsOpen } = useContext(TooltipTriggerContext);
  return (
    <RdxTooltip.Trigger
      {...props}
      asChild={asChild}
      className="Tooltip__trigger"
      onClick={(e) => {
        onClick?.(e);
        if (enableClicks) {
          e.preventDefault();
        }
      }}
      onPointerDown={(e) => {
        onPointerDown?.(e);
        if (!enableClicks) {
          return;
        }
        if (e.defaultPrevented) {
          return;
        }
        e.preventDefault();
        setIsOpen((currentIsOpen) => !currentIsOpen);
      }}
    >
      {children}
    </RdxTooltip.Trigger>
  );
};

export interface TooltipContentProps extends RdxTooltip.TooltipContentProps {
  fitContent?: boolean;
  size?: 'sm' | 'md' | 'lg';
  usePortal?: boolean;
}

export const TooltipContent = ({
  align = 'end',
  side = 'top',
  size,
  alignOffset = -5,
  sideOffset = 5,
  fitContent,
  usePortal,
  className,
  ...props
}: TooltipContentProps) => {
  const tooltip = (
    <RdxTooltip.Content
      {...props}
      asChild
      align={align}
      side={side}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
    >
      <div
        className={cn('Tooltip__content', {
          [`${className}`]: !!className,
          [`Tooltip__content--${size}`]: !!size,
          'Tooltip__content--fit': fitContent,
        })}
      >
        {props.children}
        <RdxTooltip.Arrow
          height={6}
          width={12}
          offset={20}
          className="Tooltip__arrow"
        />
      </div>
    </RdxTooltip.Content>
  );

  if (usePortal) {
    return <RdxTooltip.Portal>{tooltip}</RdxTooltip.Portal>;
  }

  return tooltip;
};
